import { Link } from 'gatsby';
import { getImage } from 'gatsby-plugin-image';
import React, { useLayoutEffect, useRef, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { formatDateMedium } from '@services';
import VideoSwiper from './VideoSwiper';
import ChdButton from '../Buttons/ChdButton';
import ChdButtonGroup from '../Buttons/ChdButtonGroup';
import ShareButton from '../Buttons/ShareButton';
import TextAlertsButton from '../Buttons/TextAlertsButton';
import LinkChd from '../LinkChd';
import VideoPlayer from '../players/VideoPlayer';
import VideoPlayerThumbnail from '../players/VideoPlayerThumbnail';
import ReadMoreLess from '../ReadMoreLess';
import VideoDate from '../VideoDate';

type Props = {
  globalCmsButtons: any,
  hosts: any[],
  lang: string,
  videos: any[],
  showOrEventData: any,
  stringTranslations: any,
}

function Home({
  globalCmsButtons, hosts, lang, showOrEventData, stringTranslations, videos,
} : Props) {
  const isMobile = useMediaQuery({
    query: '(max-width: 767px)',
  });
  const [loadVideoPlayer, setLoadVideoPlayer] = useState<boolean>(false);
  const stickyVideo = useRef<any>();
  const hostNames = hosts?.map((host: any) => host.name).join(', ');
  const latestVideo = videos[0];
  const videoThumbnailImage = latestVideo?.thumbnailMediaStandard?.gatsbyImageData
    ? getImage(latestVideo.thumbnailMediaStandard.gatsbyImageData)
    : null;

  const defenderLink = latestVideo?.defenderLink;
  if (defenderLink && defenderLink?.title) {
    defenderLink.title = globalCmsButtons['Read In The Defender'][lang].title;
  }
  const donateLinkDetails = globalCmsButtons.Donate[lang];
  const signUpLinkDetails = globalCmsButtons['Free Sign-Up'][lang];

  const latestVideoPublishDateAndTime = formatDateMedium(latestVideo?.publishDate || latestVideo?.createdAt, latestVideo?.node_locale) ?? '';

  // eslint-disable-next-line consistent-return
  useLayoutEffect(() => {
    if (isMobile) {
      const stickyVideoCleanup = (checkStickyVideo: any) => {
        if (isMobile) {
          const videoContainer = document.getElementById('videoContainer');
          videoContainer?.removeEventListener('scroll', checkStickyVideo);
        }
      };
      const videoContainer = document.getElementById('videoContainer');
      const chatContainer = document.getElementById('chatContainer');
      const initialVideoFromTop = stickyVideo.current.offsetTop;
      const checkStickyVideo = () => {
        if (window.scrollY > initialVideoFromTop + 200) {
          videoContainer?.classList.add(
            'fixed',
            'top-[55px]',
            'right-[25px]',
            'w-1/2',
            'z-[99999]',
          );
          chatContainer?.classList.add('mt-36');
        } else {
          videoContainer?.classList.remove(
            'fixed',
            'top-[55px]',
            'right-[25px]',
            'w-1/2',
            'z-[99999]',
          );
          chatContainer?.classList.remove('mt-36');
        }
      };
      window.addEventListener('scroll', checkStickyVideo);
      return stickyVideoCleanup(checkStickyVideo);
    }
  }, []);

  return (
    <div>
      {latestVideo && (
        <div id="latest-episode" className="flex flex-col lg:space-x-10 lg:flex-row">
          <div className="lg:w-2/3">
            <div
              id="videoContainer"
              ref={stickyVideo}
              className="flex-auto"
            >
              {loadVideoPlayer ? (
                <VideoPlayer
                  playbackId={latestVideo.videoPlaybackId}
                  playerName="latestVideo-player"
                  video={latestVideo}
                  autoPlay
                />
              ) : (
                <VideoPlayerThumbnail
                  altText={latestVideo.title}
                  btnOnclick={() => setLoadVideoPlayer(true)}
                  image={videoThumbnailImage}
                  video={latestVideo}
                />
              )}
            </div>
            <div className="flex justify-start my-5 text-sm uppercase">
              {latestVideoPublishDateAndTime && <VideoDate date={latestVideoPublishDateAndTime} />}
              <ShareButton
                globalCmsButtons={globalCmsButtons}
                lang={lang}
              />
              {latestVideo.podcastListenUrl && (
                <LinkChd
                  linkStyles="font-semibold tracking-wider text-center uppercase text-chdGreen-light hover:text-chdGreen-hover"
                  href={latestVideo.podcastListenUrl}
                >
                  {globalCmsButtons['Spotify Listen'][lang].title}
                </LinkChd>
              )}
            </div>
            <ChdButtonGroup className="hidden mb-6 lg:flex">
              <ChdButton
                link={donateLinkDetails}
                colorTheme="blue"
              />
              <TextAlertsButton
                lang={lang}
                globalCmsButtons={globalCmsButtons}
                className="px-2 py-4 bg-chdGreen-light hover:bg-chdGreen-hover"
              />
              <ChdButton
                link={signUpLinkDetails}
                colorTheme="green"
                linkStyles="xl:bg-chdBlue-light xl:hover:bg-[#3FB6E8] xl:border-[#0080A3] "
              />
              {defenderLink && (
              <ChdButton
                link={defenderLink}
                colorTheme="blue"
                linkStyles="xl:bg-chdGreen-light xl:hover:bg-chdGreen-hover"
              />
              )}
            </ChdButtonGroup>
          </div>
          <div id="latest-video-description" className="mb-6 lg:w-1/3">
            <Link to={latestVideo.slug}>
              <h1 className="font-serif text-[28px] mb-5 text-white hover:text-chdGreen-light font-bold tracking-wide">{latestVideo.title}</h1>
            </Link>
            <ReadMoreLess
              descriptionStyles="flex flex-col w-full md:flex md:flex-auto"
              text={latestVideo?.description?.childMarkdownRemark.html}
              stringTranslations={stringTranslations}
              lang={showOrEventData.node_locale}
            />
            {hostNames && (
            <div>
              <p
                className="mb-[5px] text-[16px] leading-[22px]"
                id="content-hosts"
              >
                {stringTranslations?.show_host[lang]}
                :
                {' '}
                {hostNames}
              </p>
            </div>
            )}
            <p className="text-[16px] leading-[22px] mt-4">
              <em>
                {stringTranslations?.show_disclaimer[lang]}
              </em>
            </p>
          </div>
          <ChdButtonGroup className="mb-6 lg:hidden">
            <ChdButton
              link={donateLinkDetails}
              colorTheme="blue"
            />
            <TextAlertsButton
              lang={lang}
              globalCmsButtons={globalCmsButtons}
              className="px-2 py-4 bg-chdGreen-light hover:bg-chdGreen-hover"
            />
            <ChdButton
              link={signUpLinkDetails}
              colorTheme="green"
              linkStyles="xl:bg-chdBlue-light xl:hover:bg-[#3FB6E8] xl:border-[#0080A3] "
            />
            {defenderLink && (
            <ChdButton
              link={defenderLink}
              colorTheme="blue"
              linkStyles="xl:bg-chdGreen-light xl:hover:bg-chdGreen-hover"
            />
            )}
          </ChdButtonGroup>
        </div>
      )}
      <VideoSwiper
        id={showOrEventData.id}
        title={stringTranslations['latest-videos'][lang]}
        showDate
        videos={videos.slice(1, 21)}
      />
    </div>

  );
}

export default Home;
